import React, { useState, useEffect, useCallback, useContext } from "react";
import DOMPurify from "dompurify";
import { LGrid, LButton, LDialog, LFade } from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AuthContext } from "../../../../../context/AuthContext";
import NotificationContext from "../../../../../context/NotificationContext";
import KeyBasedLButton from "../../../../Shared/KeyBasedLButton";
import LoadingOverlay from "../../../../Shared/LoadingOverlay/LoadingOverlay";
import { EmailMessageForm } from "../EmailMessageForm/EmailMessageForm";
import { EMAIL_TEMPLATES_API } from "../constants";
import styles from "./EmailMessageTable.module.css";

const GATEWAY_BASE = process.env.REACT_APP_API_GATEWAY_BASE;

function EmailMessageTable() {
  const authContext = useContext(AuthContext);
  const notificationContext = useContext(NotificationContext);

  const [headers, setHeaders] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [state, setState] = useState({
    authLoaded: false,
    headersLoaded: false,
    rebindStatus: true,
    displayForm: false,
    selectedTemplate: {},
    previewTemplate: null,
  });

  const loadingPanel = <LoadingOverlay loadingText="Loading" />;

  useEffect(() => {
    let checkForUser = setInterval(() => {
      if (authContext.isLoaded() && authContext.authState()) {
        setHeaders({
          Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
        });
        setState((s) => ({ ...s, authLoaded: true, headersLoaded: true }));
        clearInterval(checkForUser);
      }
    }, 100);

    return () => clearInterval(checkForUser);
  }, [authContext]);

  const getAllTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      let url = GATEWAY_BASE + EMAIL_TEMPLATES_API;
      const response = await fetch(url, { mode: "cors", headers: headers });
      const json = await response.json();
      setTemplates(json);
    } catch (error) {
      console.log(error);
      notificationContext.addError("Failed to load email templates.");
    }
    setIsLoading(false);
  }, [headers, notificationContext]);

  useEffect(() => {
    if (state.headersLoaded && state.rebindStatus) {
      getAllTemplates();
      setState((s) => ({ ...s, rebindStatus: false }));
    }
  }, [state.headersLoaded, state.rebindStatus, getAllTemplates]);

  const displayForm = (templateGuid, display) => {
    if (templateGuid) {
      fetch(GATEWAY_BASE + EMAIL_TEMPLATES_API + "/" + templateGuid, {
        method: "GET",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
        },
      })
        .then((res) => res.json())
        .then((result) => {
          setState((s) => ({
            ...s,
            displayForm: display,
            selectedTemplate: result,
          }));
        });
    } else {
      setState((s) => ({
        ...s,
        displayForm: display,
        selectedTemplate: {},
      }));
    }
  };

  const afterSave = () => {
    setState((s) => ({
      ...s,
      displayForm: false,
      rebindStatus: true,
      selectedTemplate: {},
    }));
    notificationContext.addSuccess("Email template successfully saved.");
  };

  const closeForm = () => {
    setState((s) => ({
      ...s,
      displayForm: false,
      selectedTemplate: {},
    }));
  };

  const editCell = (value) => {
    return (
      <td>
        <KeyBasedLButton
          className="green"
          themeColor="primary"
          onClick={() => displayForm(value.dataItem.emailTemplateGuid, true)}
          buttonText="Edit"
          parentKey={value.dataItem.emailTemplateGuid}
        />
      </td>
    );
  };

  const statusCell = (value) => {
    return (
      <td>
        <span>{value.dataItem.isActive ? "Active" : "Inactive"}</span>
      </td>
    );
  };

  const dateCell = (fieldName) => (value) => {
    var formattedDate = value.dataItem[fieldName]
      ? new Date(value.dataItem[fieldName])
      : null;
    return (
      <td>
        {formattedDate &&
          formattedDate.getMonth() + 1 +
            "/" +
            formattedDate.getDate() +
            "/" +
            formattedDate.getFullYear()}
      </td>
    );
  };

  const previewCell = (value) => {
    return (
      <td>
        <LButton
          look="flat"
          onClick={() =>
            setState((s) => ({ ...s, previewTemplate: value.dataItem }))
          }
        >
          Preview
        </LButton>
      </td>
    );
  };

  const auditTrailCell = (value) => {
    return (
      <td style={{ textAlign: "center" }}>
        <FontAwesomeIcon
          icon="fa-solid fa-book"
          style={{ fontSize: "18px", cursor: "pointer" }}
          onClick={() => {}}
        />
      </td>
    );
  };

  const wrappedHeaderCell = (title) => (props) => {
    return (
      <span style={{ whiteSpace: "normal", textAlign: "center", display: "block" }}>
        {title}
      </span>
    );
  };

  const notesCell = (value) => {
    return (
      <td>
        <span>—</span>
      </td>
    );
  };

  return (
    <div className="relative-div">
      {isLoading && loadingPanel}
      {state.authLoaded ? (
        <div>
          <h3>Manage Email & Messaging</h3>
          <div className={styles.emailMessageActions}>
            <LButton
              themeColor="primary"
              className="green"
              onClick={() => displayForm(null, true)}
            >
              Add Email Template
            </LButton>
          </div>

          {!isLoading && templates.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
              No email templates found. Click "Add Email Template" to create one.
            </div>
          ) : (
          <LGrid
            style={{ height: "500px" }}
            data={templates}
            sortable
            filterable
            pageable
            columns={[
              {
                field: "emailTemplateGuid",
                title: " ",
                cell: editCell,
                filterable: false,
                sortable: false,
                width: "80px",
              },
              {
                field: "isActive",
                title: "Status",
                cell: statusCell,
                width: "100px",
              },
              { field: "name", title: "Name" },
              { field: "messageType", title: "Message Type", width: "160px" },
              {
                field: "body",
                title: "Message Config",
                cell: previewCell,
                filterable: false,
                sortable: false,
                width: "130px",
              },
              {
                field: "createdDate",
                title: "Added On",
                cell: dateCell("createdDate"),
                width: "130px",
              },
              { field: "createdByName", title: "Added By", width: "150px" },
              {
                field: "lastUpdatedDate",
                title: "Updated On",
                cell: dateCell("lastUpdatedDate"),
                width: "130px",
              },
              { field: "lastUpdatedByName", title: "Updated By", width: "150px" },
              {
                title: "Compliance Approved On",
                headerCell: wrappedHeaderCell("Compliance Approved On"),
                width: "120px",
                filterable: false,
                sortable: false,
              },
              {
                title: "Compliance Approved By",
                headerCell: wrappedHeaderCell("Compliance Approved By"),
                width: "120px",
                filterable: false,
                sortable: false,
              },
              {
                title: "Internal Note(s)",
                cell: notesCell,
                headerCell: wrappedHeaderCell("Internal Note(s)"),
                filterable: false,
                sortable: false,
                width: "100px",
              },
              {
                title: "Audit Trail",
                cell: auditTrailCell,
                headerCell: wrappedHeaderCell("Audit Trail"),
                filterable: false,
                sortable: false,
                width: "80px",
              },
            ]}
          />
          )}

          <LFade transitionEnterDuration={200}>
            {state.previewTemplate && (
              <LDialog
                width="600"
                title={state.previewTemplate.name}
                body={
                  <>
                    <style>{`#email-popup-preview p { margin: 0 0 1em 0; } #email-popup-preview p:last-child { margin-bottom: 0; }`}</style>
                    <div
                      id="email-popup-preview"
                      className={styles.previewBody}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(state.previewTemplate.body) }}
                    />
                  </>
                }
                actionLayout="end"
                action={
                  <LButton
                    onClick={() =>
                      setState((s) => ({ ...s, previewTemplate: null }))
                    }
                  >
                    Close
                  </LButton>
                }
                actionCloseButton={() =>
                  setState((s) => ({ ...s, previewTemplate: null }))
                }
                actionOverlay={() =>
                  setState((s) => ({ ...s, previewTemplate: null }))
                }
              />
            )}
          </LFade>

          <LFade transitionEnterDuration={200}>
            {state.displayForm && (
              <EmailMessageForm
                selectedTemplate={state.selectedTemplate}
                saveForm={afterSave}
                closeForm={closeForm}
              />
            )}
          </LFade>
        </div>
      ) : null}
    </div>
  );
}

export { EmailMessageTable };
